/**
 * SourceDocumentViewer - a streamed source, rendered as what it actually is.
 *
 * Most of the corpus is PDFs converted to HTML: whole documents carrying
 * their own `<head>`, an embedded `<style>`, and absolutely-positioned
 * `#page_N` divs. Those go into a SANDBOXED IFRAME, for two independent
 * reasons:
 *
 *   • Isolation - the converter's CSS styles bare `body` and `#page_N`. Put
 *     inline, it would restyle the app around it.
 *   • Safety - the frame carries no `allow-scripts`, so nothing in a fetched
 *     document can execute, whatever it contains.
 *
 * Plain text and markdown keep the path they already had.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiFileTextLight, PiXLight } from 'react-icons/pi';
import {
  LocatorTarget,
  clearHighlight,
  findPassage,
  isExternalUrl,
  revealPassage,
} from '../citationMarkers';
import { SourceKind, sourceFacts } from '../sourceDocument';
import SourceBody from './SourceBody';

interface Props {
  title?: string;
  kind: SourceKind;
  /** Named on the fallback card when a format cannot be shown inline. */
  fileName?: string;
  contentType?: string;
  /** Object URL for the streamed bytes - what a PDF is rendered from. */
  url?: string | null;
  /** The decoded document. */
  text: string;
  /** The citation this came from - its metadata dresses the header. */
  citation?: Record<string, any> | null;
  /** `[N]` in the answer, when the source was cited rather than searched. */
  citationNumber?: number;
  /**
   * Where the cited passage is, best target first.
   *
   * A chain rather than one target: offsets go stale, text fragments are
   * Chromium-only, and the quote is the form that actually survives
   * conversion. The viewer keeps the first one that finds the passage.
   */
  locators?: LocatorTarget[];
  onClose: () => void;
  /** False inside a pane that already has its own close (Research): one ✕, not two. */
  showClose?: boolean;
}

const SourceDocumentViewer: React.FC<Props> = ({
  title,
  kind,
  fileName,
  contentType,
  url,
  text,
  citation,
  citationNumber,
  locators,
  onClose,
  showClose = true,
}) => {
  const { t } = useTranslation();
  // Only the fields that are actually present - a sparse citation gets a
  // short header, never a row of "Unknown".
  const facts = useMemo(() => sourceFacts(citation), [citation]);

  const frameRef = useRef<HTMLIFrameElement | null>(null);
  /** The in-app scroller for the text/markdown path - not everything is framed. */
  const textRef = useRef<HTMLDivElement | null>(null);
  const [landed, setLanded] = useState<LocatorTarget['kind'] | null>(null);
  /** The chain we have already walked, so a re-render does not re-scroll. */
  const walkedRef = useRef<LocatorTarget[] | null>(null);

  /**
   * Walk the locator chain against a rendered document and land on the first
   * target that actually finds the passage.
   *
   * Every branch ends in a text search, because that is the only thing the
   * rendered document can be asked about directly:
   *
   *   quote / text_fragment → the verbatim passage, searched for
   *   char_range            → the offsets are used to CUT a probe out of the
   *                           rendered text, then that probe is searched for
   *                           - so offsets that are merely approximate still
   *                           work, and offsets that are nonsense (0..0 after
   *                           a chunk merge) simply find nothing and fall
   *                           through to the quote behind them
   *   heading               → a heading whose text contains it
   *   chunk                 → the converted `#page_N` div, the only stable
   *                           chunk boundary these documents have
   *
   * Nothing here can fail loudly. A document that cannot be aimed at opens at
   * the top, which is what it did before any of this existed.
   */
  const scrollToPassage = useCallback((root: Document | HTMLElement | null | undefined) => {
    const chain = locators || [];
    if (!root) return;

    const scope: HTMLElement | null =
      (root as Document).body !== undefined
        ? (root as Document).body
        : (root as HTMLElement);
    if (!scope) return;

    clearHighlight(root);

    for (const target of chain) {
      let needle = '';

      if (target.kind === 'quote') {
        needle = target.quote;
      } else if (target.kind === 'text_fragment') {
        // A text fragment is a quote we can also search for, so a browser
        // without fragment support still lands in the right place.
        needle = decodeURIComponent(
          (target.url.split('#:~:text=')[1] || '').split('&')[0]
        );
      } else if (target.kind === 'char_range') {
        // Cut a probe from the RENDERED text. Offsets taken over extracted
        // text rarely line up exactly, so the slice is used as a search
        // string, never as a scroll position.
        needle = (scope.textContent || '').slice(target.start, target.end).trim().slice(0, 160);
      } else if (target.kind === 'heading') {
        const headings = Array.from(
          scope.querySelectorAll<HTMLElement>('h1,h2,h3,h4,h5,h6,b,strong')
        );
        const match = headings.find((h) =>
          (h.textContent || '').trim().toLowerCase().includes(target.heading.toLowerCase())
        );
        if (match) {
          revealPassage(root, match);
          setLanded('heading');
          return;
        }
        continue;
      } else if (target.kind === 'chunk') {
        const pages = Array.from(scope.querySelectorAll<HTMLElement>('[id^="page_"]'));
        const page = pages[target.chunk];
        if (page) {
          revealPassage(root, page);
          setLanded('chunk');
          return;
        }
        continue;
      } else {
        // 'top' - the end of the chain.
        break;
      }

      const hit = needle.trim() ? findPassage(root, needle) : null;
      if (hit) {
        revealPassage(root, hit);
        setLanded(target.kind);
        return;
      }
    }

    // Nothing matched: the document opens at the top, which is honest.
    setLanded('top');
  }, [locators]);

  /** The framed document has to exist before it can be searched. */
  const onFrameLoad = useCallback(() => {
    walkedRef.current = locators || null;
    scrollToPassage(frameRef.current?.contentDocument);
  }, [locators, scrollToPassage]);

  /**
   * Re-aim when the citation changes but the document does not.
   *
   * Clicking a second `[n]` into the same source - or one of the jump
   * sections - swaps the locator without reloading the frame, so `onLoad`
   * never fires again. Without this the pane stayed parked on the first
   * passage and the second click appeared to do nothing.
   */
  useEffect(() => {
    if (!locators || walkedRef.current === locators) return;
    const framed = frameRef.current?.contentDocument;
    const inline = textRef.current;
    // Only once something is actually rendered; the load/mount path covers
    // the first pass.
    if (framed?.body?.childNodes.length) {
      walkedRef.current = locators;
      scrollToPassage(framed);
    } else if (inline) {
      walkedRef.current = locators;
      scrollToPassage(inline);
    }
  }, [locators, kind, text, scrollToPassage]);


  return (
    <div className="h-full flex flex-col min-h-0" data-testid="source-document">
      <header className="flex-shrink-0 px-3 py-2 border-b border-hairline-soft space-y-1.5">
        <div className="flex items-start gap-2">
          <PiFileTextLight className="w-4 h-4 mt-0.5 text-fg-muted flex-shrink-0" aria-hidden />
          <div className="flex-1 min-w-0">
            <h3 className="truncate text-sm font-medium text-fg-strong"
                data-testid="source-title">
              {title || t('Source')}
            </h3>
            {/* Only a real address. A stored path here became a localhost
                link the moment the browser resolved it. */}
            {isExternalUrl(citation?.url) && (
              <a
                href={citation!.url}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="source-origin"
                className="block truncate text-[10px] text-fg-muted hover:text-champagne
                           transition-colors duration-200 ease-slide"
              >
                {citation!.url}
              </a>
            )}
          </div>

          {typeof citationNumber === 'number' && citationNumber > 0 && (
            <span
              data-testid="source-citation-number"
              title={t('Cited as')}
              className="px-1.5 py-0 rounded-full text-[10px] font-medium flex-shrink-0
                         bg-champagne/15 border border-champagne/40 text-fg-strong"
            >
              [{citationNumber}]
            </span>
          )}

{showClose && (
          <button
            type="button"
            onClick={onClose}
            data-testid="source-close"
            aria-label={t('Close')}
            className="p-1 rounded-md text-fg-muted hover:text-fg-strong flex-shrink-0
                       transition-colors duration-200 ease-slide"
          >
            <PiXLight className="w-4 h-4" />
          </button>
          )}
        </div>

        {facts.length > 0 && (
          <dl className="flex flex-wrap items-center gap-x-3 gap-y-1"
              data-testid="source-facts">
            {facts.map((fact) => (
              <div key={fact.label} className="flex items-baseline gap-1 min-w-0"
                   data-testid="source-fact" data-label={fact.label}>
                <dt className="text-[11px] text-fg-muted">
                  {t(fact.label)}
                </dt>
                <dd className="text-[11px] text-fg-body truncate max-w-[16rem]">
                  {fact.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </header>

      <div className="flex-1 min-h-0">
        {/* Every format goes through the shared renderer; this component owns
            only the header and the locator. */}
        <SourceBody
          kind={kind}
          text={text}
          url={url}
          title={title}
          fileName={fileName || citation?.file_name}
          contentType={contentType}
          frameRef={frameRef}
          onFrameLoad={onFrameLoad}
          textRef={textRef}
          landed={landed || undefined}
        />
      </div>
    </div>
  );
};

export default SourceDocumentViewer;
